/**
 * Jovii — what a release NAME and the resolve payload can honestly tell us.
 *
 * Pure functions, no state, no DOM, unit tested. Two defects live and die here:
 *
 * SEASON PACKS (docs/OPEN-DEFECTS.md I11). For a season pack the scraper reports the
 * WHOLE torrent's size as the episode's, so the derived bitrate reads 5–20× higher
 * than it is and good series releases are ranked down. Measured against the raw
 * AIOStreams payload (2026-08-15, Chernobyl S1E1):
 *
 *   - MediaFusion reports `videoSize` = the torrent. A pack it RECOGNISED (no episode
 *     file picked, e.g. `Chernobyl S01 … x264-LRL`) gets its 📊 bitrate computed over
 *     the pack's TOTAL duration — so `sizeBytes·8 ÷ bitrateBps` recovers that total,
 *     and dividing by one episode's runtime recovers the FILE COUNT from the payload
 *     itself. 14.9 GB at 6.53 Mbps over a 3651 s episode → 5.007 → 5 files.
 *   - A pack it did NOT recognise (episode filename, torrent size — e.g.
 *     `Chernobyl.S01E01…playHD.mkv` advertised at 48.3 GB / 106 Mbps) carries no such
 *     signal. 106 Mbps for 1080p AVC is physically implausible (BluRay video peaks
 *     ~40 Mbps), so an implausible bitrate on an episode-named series release is
 *     read as "priced as the pack" and divided by the season's episode count.
 *   - Torrentio's `videoSize` is the file's and needs nothing.
 *
 * The division is for RANKING and DISPLAY: ±15% error is fine where 5–20× was not.
 * The probe's Content-Range remains the only measured size.
 *
 * DEAD PLACEHOLDERS (docs/OPEN-DEFECTS.md I12). 46 of 80 sampled candidates were
 * dead-link placeholders, and there are only FOUR distinct placeholder objects, all
 * under 3 MB, recurring verbatim across unrelated titles (docs/METADATA.md). One
 * `bytes=0-0` request answers with the real total in Content-Range, and
 * `deadSizeVerdict` is the whole decision. The fetch itself lives in
 * `picker.svelte.ts`; the judgement is here so it can be tested.
 */

import type { Candidate, ContentRef, QualityTier, Season } from '$types';

/* ────────────────────────────────────────────────────────────────────────────
 * Season packs
 * ──────────────────────────────────────────────────────────────────────────── */

/** What a release name says about how many episodes are inside. */
export type ReleaseSpan =
  | { kind: 'single' }
  | { kind: 'episodes'; count: number }
  | { kind: 'season'; season: number }
  | { kind: 'seasons'; from: number; to: number };

/**
 * Parse the span out of a release name. Order matters: `S01E01-E05` contains
 * `S01E01`, which contains `S01`, so the widest specific pattern goes first.
 */
export function releaseSpan(title: string): ReleaseSpan {
  const t = title.replace(/[._]/g, ' ');

  // S01E01-E05 / S01E01-05 / E01-E26
  const range = /\bS?(\d{1,2})?\s*E(\d{1,3})\s*[-~]\s*E?(\d{1,3})\b/i.exec(t);
  if (range && range[2] !== undefined && range[3] !== undefined) {
    const from = Number.parseInt(range[2], 10);
    const to = Number.parseInt(range[3], 10);
    if (to > from && to - from < 60) return { kind: 'episodes', count: to - from + 1 };
  }

  // S01E01 / 1x01 / E01 alone: a single episode, by name.
  if (/\bS\d{1,2}\s*E\d{1,3}\b/i.test(t)) return { kind: 'single' };
  if (/\b\d{1,2}x\d{2,3}\b/i.test(t)) return { kind: 'single' };

  // S01-S05 / Seasons 1-5: several whole seasons.
  const multi =
    /\bS(\d{1,2})\s*[-~]\s*S(\d{1,2})\b/i.exec(t) ??
    /\bSeasons?\s+(\d{1,2})\s*[-~]\s*(\d{1,2})\b/i.exec(t);
  if (multi && multi[1] !== undefined && multi[2] !== undefined) {
    const from = Number.parseInt(multi[1], 10);
    const to = Number.parseInt(multi[2], 10);
    if (to > from && to - from < 40) return { kind: 'seasons', from, to };
  }

  // S01 / Season 1, with no episode anywhere: a whole season.
  const season = /\bS(\d{1,2})\b/i.exec(t) ?? /\bSeason\s+(\d{1,2})\b/i.exec(t);
  if (season && season[1] !== undefined) {
    return { kind: 'season', season: Number.parseInt(season[1], 10) };
  }

  if (/\bE\d{2,3}\b/i.test(t) || /\bEp(?:isode)?\s*\d{1,3}\b/i.test(t)) return { kind: 'single' };
  if (/\bcomplete\b/i.test(t)) return { kind: 'season', season: 0 };

  return { kind: 'single' };
}

/**
 * The heaviest AVERAGE bitrate a single file of this tier plausibly carries.
 * 1080p BluRay video peaks around 40 Mbps; a 2160p remux can reach ~100–130.
 * Above these, a series candidate is priced as its whole pack.
 */
export function plausibleBitrateBps(quality: QualityTier): number {
  switch (quality) {
    case '2160p':
      return 130_000_000;
    case '1080p':
      return 40_000_000;
    case '720p':
      return 20_000_000;
    case '480p':
      return 10_000_000;
    default:
      return 40_000_000;
  }
}

/** Below this, a per-episode estimate is nonsense and the division is refused. */
const MIN_EPISODE_BYTES = 150e6;

/** What to assume a thing runs for before anything has been probed. */
export const FALLBACK_EPISODE_SEC = 2700;
export const FALLBACK_MOVIE_SEC = 7200;

export function fallbackDurationSec(ref: Pick<ContentRef, 'kind'>): number {
  return ref.kind === 'series' ? FALLBACK_EPISODE_SEC : FALLBACK_MOVIE_SEC;
}

/** Everything pack arithmetic needs to know about the thing being watched. */
export interface PackContext {
  ref: ContentRef;
  /** Best known runtime of ONE episode (or the film), seconds. 0 = unknown. */
  durationSec: number;
  /** Episodes in the season being watched, from Cinemeta. null = unknown. */
  episodesInSeason: number | null;
}

export interface PackAdjusted {
  /** Estimated size of the EPISODE'S file, bytes. `c.sizeBytes` untouched for a single. */
  sizeBytes: number;
  /** Recomputed average bitrate to match. */
  bitrateBps: number;
  /** True when the numbers were divided down from a pack's. */
  isPack: boolean;
  /** How many files the division assumed. 1 for a single. */
  files: number;
}

/**
 * A candidate's numbers per EPISODE rather than per torrent. Three rungs, best
 * evidence first; a movie or an unrecognised shape passes through untouched.
 */
export function packAdjusted(c: Candidate, ctx: PackContext): PackAdjusted {
  const single: PackAdjusted = {
    sizeBytes: c.sizeBytes,
    bitrateBps: c.bitrateBps,
    isPack: false,
    files: 1
  };
  if (ctx.ref.kind !== 'series' || c.sizeBytes <= 0) return single;
  const dur = ctx.durationSec > 0 ? ctx.durationSec : FALLBACK_EPISODE_SEC;

  // Rung 1 — the payload's own arithmetic. When the aggregator knew the entry was a
  // pack it computed 📊 over the pack's total duration, so size·8÷bitrate÷runtime IS
  // the file count. Only trusted against a real runtime: against the guessed one, a
  // single 90-minute file would read as a two-file pack.
  let files = 0;
  let bitratePerFile = false;
  if (c.bitrateBps > 0 && ctx.durationSec > 0) {
    const n = (c.sizeBytes * 8) / c.bitrateBps / dur;
    if (n >= 1.7 && n < 80) {
      files = Math.max(2, Math.round(n));
      bitratePerFile = true; // the advertised bitrate is already the per-file one
    }
  }

  // Rung 2 — the release name.
  if (files < 2) {
    const span = releaseSpan(c.title);
    if (span.kind === 'episodes') files = span.count;
    else if (span.kind === 'season') files = ctx.episodesInSeason ?? 0;
    else if (span.kind === 'seasons' && ctx.episodesInSeason !== null) {
      files = ctx.episodesInSeason * (span.to - span.from + 1);
    }
  }

  // Rung 3 — an episode-named release priced like its pack (MediaFusion reports the
  // torrent's size against the file). Physically implausible bitrate is the tell.
  if (files < 2 && ctx.episodesInSeason !== null && ctx.episodesInSeason >= 2) {
    const impliedBps = (c.sizeBytes * 8) / dur;
    if (impliedBps > plausibleBitrateBps(c.quality)) files = ctx.episodesInSeason;
  }

  if (files < 2) return single;
  const sizeBytes = Math.round(c.sizeBytes / files);
  if (sizeBytes < MIN_EPISODE_BYTES) return single;
  const bitrateBps = bitratePerFile && c.bitrateBps > 0 ? c.bitrateBps : Math.round((sizeBytes * 8) / dur);
  return { sizeBytes, bitrateBps, isPack: true, files };
}

/** Episodes in one season, from Cinemeta's list. null when the season is unknown. */
export function episodesIn(seasons: readonly Season[], season: number | undefined): number | null {
  if (season === undefined) return null;
  const hit = seasons.find((s) => s.season === season);
  if (!hit || hit.episodes.length === 0) return null;
  return hit.episodes.length;
}

/** Cinemeta's `runtime` string ("136 min") → seconds. 0 when unparseable. */
export function runtimeSecFromText(text: string | undefined): number {
  if (!text) return 0;
  const m = /(\d+)\s*min/i.exec(text);
  if (!m || m[1] === undefined) return 0;
  const mins = Number.parseInt(m[1], 10);
  return Number.isFinite(mins) && mins > 0 ? mins * 60 : 0;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Languages — matching a filter against what a release claims
 * ──────────────────────────────────────────────────────────────────────────── */

/** Three-letter (and scene-word) tags → ISO 639-1, so hints and probed tracks meet. */
const TO_ISO2: Record<string, string> = {
  eng: 'en', en: 'en',
  fre: 'fr', fra: 'fr', fr: 'fr',
  spa: 'es', esp: 'es', es: 'es',
  ger: 'de', deu: 'de', de: 'de',
  ita: 'it', it: 'it',
  por: 'pt', pt: 'pt',
  rus: 'ru', ru: 'ru',
  jpn: 'ja', jap: 'ja', ja: 'ja',
  kor: 'ko', ko: 'ko',
  chi: 'zh', zho: 'zh', zh: 'zh', chs: 'zh', cht: 'zh',
  hin: 'hi', hi: 'hi',
  ara: 'ar', ar: 'ar',
  dut: 'nl', nld: 'nl', nl: 'nl',
  pol: 'pl', pl: 'pl',
  swe: 'sv', sv: 'sv',
  nor: 'no', nob: 'no', no: 'no',
  dan: 'da', da: 'da',
  fin: 'fi', fi: 'fi',
  tur: 'tr', tr: 'tr',
  cze: 'cs', ces: 'cs', cs: 'cs',
  gre: 'el', ell: 'el', el: 'el',
  heb: 'he', he: 'he',
  tha: 'th', th: 'th',
  ukr: 'uk', uk: 'uk',
  hun: 'hu', hu: 'hu',
  rum: 'ro', ron: 'ro', ro: 'ro',
  vie: 'vi', vi: 'vi',
  ind: 'id', id: 'id'
};

/** Normalise any language tag — "eng", "fr", "fra", "CHS" — to ISO 639-1-ish.
 *  '' for the tags that name no single language ("und", "multi"). */
export function iso2(tag: string): string {
  const key = tag.trim().toLowerCase();
  if (key === '' || key === 'und' || key === 'multi') return '';
  return TO_ISO2[key] ?? TO_ISO2[key.slice(0, 3)] ?? key.slice(0, 2);
}

/** Do two tags name the same language, whatever their length? */
export function sameLang(a: string, b: string): boolean {
  const na = iso2(a);
  const nb = iso2(b);
  return na !== '' && na === nb;
}

/**
 * Subtitle-language hints from a release name. Scene vocabulary only — VOSTFR,
 * KORSUB, CHS-ENG, MULTi-SUBS — so this is sparse on purpose: a hint that fires
 * is usually right, and the probe's real subtitle tracks take over once a row has
 * been verified. 'multi' means "subtitles in several languages, unnamed".
 */
export function subtitleLangHints(title: string): string[] {
  const t = title.replace(/[._]/g, ' ');
  const out = new Set<string>();

  if (/\bVOSTFR\b/i.test(t)) out.add('fr');
  if (/\bVOST(?:A|EN)\b/i.test(t)) out.add('en');
  if (/\bCHS\b|\bCHT\b/i.test(t)) out.add('zh');
  if (/\bCH[ST]\s*-\s*ENG?\b/i.test(t)) out.add('en'); // CHS-ENG: chinese + english subs
  if (/\bE-?SUBS?\b/i.test(t)) out.add('en');
  if (/\bM-?SUBS?\b|\bMULTI\s*-?\s*SUBS?\b|\bMULTISUBS?\b|\bSUBBED\b/i.test(t)) out.add('multi');

  // ENGSUB / SWESUB / KORSUB / NLSUB …: a language token glued to SUB(S).
  const glued = t.matchAll(/\b([A-Z]{2,3})\s?-?SUBS?\b/gi);
  for (const m of glued) {
    const token = (m[1] ?? '').toLowerCase();
    if (token === 'hc') continue; // hardcoded marker, language unknown
    const lang = TO_ISO2[token];
    if (lang) out.add(lang);
  }

  return [...out];
}

/* ────────────────────────────────────────────────────────────────────────────
 * Dead placeholders — the judgement half of the bytes=0-0 screen
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Sizes of the placeholder objects Real-Debrid serves in place of a dead link,
 * measured verbatim across unrelated titles (docs/METADATA.md, incidental finding 1;
 * docs/PROJECT-CONTEXT.md for Comet's). Exact matches are conclusive.
 */
export const PLACEHOLDER_SIZES: ReadonlySet<number> = new Set([
  410_511, // Comet's failed_opening_v2.mp4
  2_703_278,
  1_543_594,
  2_119_075,
  23_172
]);

/**
 * Below this, the "file" cannot be a film or an episode — every observed placeholder
 * sits under 3 MB and a real 20-minute episode at a miserly 1 Mbps is 150 MB.
 * 10 MB per docs/OPEN-DEFECTS.md I12.
 */
export const MIN_REAL_BYTES = 10 * 1000 * 1000;

/** Is a file of this TOTAL size a dead placeholder or plausibly the film? */
export function deadSizeVerdict(totalBytes: number): 'dead' | 'plausible' {
  if (!Number.isFinite(totalBytes) || totalBytes < 0) return 'plausible';
  if (PLACEHOLDER_SIZES.has(totalBytes)) return 'dead';
  return totalBytes < MIN_REAL_BYTES ? 'dead' : 'plausible';
}

/** `Content-Range: bytes 0-0/48311823452` → 48311823452. null when absent/opaque. */
export function totalFromContentRange(header: string | null): number | null {
  if (!header) return null;
  const m = /bytes\s+(?:\d+-\d+|\*)\/(\d+)/i.exec(header);
  if (!m || m[1] === undefined) return null;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}
